import { Routes, Route } from 'react-router-dom';
import { SignIn, SignUp } from '@clerk/clerk-react';
import { LandingPage } from './pages/LandingPage';
import { DashboardPage } from './pages/DashboardPage';
import { SubscriptionPage } from './pages/SubscriptionPage';
import { WebsiteDetailPage } from './pages/WebsiteDetailPage';
import { PrivacyPage } from './pages/PrivacyPage';
import { TermsPage } from './pages/TermsPage';
import { ContactPage } from './pages/ContactPage';
import { Header } from './components/Header';
import { ProtectedRoute } from './components/ProtectedRoute';

// Clerk dark theme appearance matching our design system
const clerkAppearance = {
  variables: {
    colorPrimary: '#dc2626',
    colorBackground: '#16161f',
    colorInputBackground: '#12121a',
    colorInputText: '#f4f4f5',
    colorText: '#f4f4f5',
    colorTextSecondary: '#a1a1aa',
    colorDanger: '#ef4444',
    colorSuccess: '#10b981',
    colorWarning: '#f59e0b',
    borderRadius: '12px',
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
  },
  elements: {
    rootBox: {
      width: '100%',
      maxWidth: '420px',
    },
    card: {
      backgroundColor: '#16161f',
      border: '1px solid rgba(255, 255, 255, 0.08)',
      boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.6)',
    },
    headerTitle: {
      color: '#f4f4f5',
      fontSize: '1.5rem',
      fontWeight: '600',
    },
    headerSubtitle: {
      color: '#a1a1aa',
    },
    socialButtonsBlockButton: {
      backgroundColor: '#12121a',
      border: '1px solid rgba(255, 255, 255, 0.08)',
      color: '#f4f4f5',
      '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.06)',
        borderColor: 'rgba(255, 255, 255, 0.15)',
      },
    },
    dividerLine: {
      backgroundColor: 'rgba(255, 255, 255, 0.08)',
    },
    dividerText: {
      color: '#71717a',
    },
    formFieldLabel: {
      color: '#a1a1aa',
    },
    formFieldInput: {
      backgroundColor: '#12121a',
      border: '1px solid rgba(255, 255, 255, 0.08)',
      color: '#f4f4f5',
      '&:focus': {
        borderColor: '#dc2626',
        boxShadow: '0 0 0 3px rgba(220, 38, 38, 0.15)',
      },
    },
    formButtonPrimary: {
      background: 'linear-gradient(135deg, #dc2626, #f43f5e)',
      '&:hover': {
        background: 'linear-gradient(135deg, #ef4444, #fb7185)',
      },
    },
    footerActionLink: {
      color: '#dc2626',
      '&:hover': {
        color: '#ef4444',
      },
    },
    identityPreviewEditButton: {
      color: '#dc2626',
    },
    formFieldInputShowPasswordButton: {
      color: '#a1a1aa',
    },
  },
};

function App() {
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/sign-in/*" element={
        <AuthPage>
          <SignIn routing="path" path="/sign-in" appearance={clerkAppearance} />
        </AuthPage>
      } />
      <Route path="/sign-up/*" element={
        <AuthPage>
          <SignUp routing="path" path="/sign-up" appearance={clerkAppearance} />
        </AuthPage>
      } />
      <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
      <Route path="/subscription" element={<ProtectedRoute><SubscriptionPage /></ProtectedRoute>} />
      <Route path="/website/:id" element={<ProtectedRoute><WebsiteDetailPage /></ProtectedRoute>} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/contact" element={<ContactPage />} />
    </Routes>
  );
}

function AuthPage({ children }: { children: React.ReactNode }) {
  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: '#0a0a0f',
    }}>
      <Header />
      <div style={{
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '2rem',
      }}>
        {children}
      </div>
    </div>
  );
}

export default App;
